import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { config } from '../src/config';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import Button from '../src/components/Button';
import Input from '../src/components/Input';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';

/** ForgotPasswordScreen - Request a password reset link via email. */
export default function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  /** POST /api/auth/forgot-password — send reset link email. */
  const handleSubmit = async () => {
    if (!email) { setError('Please enter your email address'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.scroll}>
          <ContentContainer maxWidth={480}>
            <Text style={styles.icon}>📧</Text>
            <Text style={styles.title}>Check your email</Text>
            <Text style={styles.description}>
              If an account exists for {email}, we've sent a password reset link. Check your inbox and spam folder.
            </Text>
            <Button title="Back to Sign In" onPress={() => router.replace('/login')} size="lg" style={styles.btn} />
            <TouchableOpacity onPress={() => { setSent(false); setEmail(''); }} style={styles.linkContainer}>
              <Text style={styles.linkText}>Didn't receive it? <Text style={styles.link}>Try again</Text></Text>
            </TouchableOpacity>
          </ContentContainer>
        </ScrollView>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Forgot password?</Text>
          <Text style={styles.description}>
            Enter the email address associated with your account and we'll send you a link to reset your password.
          </Text>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Input label="Email Address" icon="mail-outline" placeholder="you@example.com" value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" maxLength={255} />
          <Button title="Send Reset Link" onPress={handleSubmit} loading={loading} size="lg" style={styles.btn} />

          <TouchableOpacity onPress={() => router.back()} style={styles.linkContainer}>
            <Text style={styles.linkText}>← Back to Sign In</Text>
          </TouchableOpacity>
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl },
  icon: { fontSize: 48, textAlign: 'center', marginBottom: spacing.md },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  description: { fontSize: fontSize.md, color: c.textSecondary, lineHeight: 24, marginBottom: spacing.lg },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  btn: { marginTop: spacing.sm },
  linkContainer: { alignItems: 'center', marginTop: spacing.lg },
  linkText: { fontSize: fontSize.sm, color: c.textSecondary },
  link: { color: c.primary, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
